// exportService - Export API client
// Implements US-6.1: Export Infrastructure and PDF Generation
// Implements US-6.2: Multi-Format Exports (PNG, CSV, JSON)
// Implements US-6.3: MinIO Integration and Download Page

import axios from 'axios';
import apiClient from '../utils/apiClient';
import { getToken } from '../utils/tokenStorage';

/**
 * Export request interface
 */
export interface ExportWorkspaceRequest {
  format: 'pdf' | 'png' | 'csv' | 'json';
}

/**
 * Export response interface
 */
export interface ExportWorkspaceResponse {
  success: boolean;
  downloadUrl: string;
  filename: string;
  message: string;
}

/**
 * Export file metadata interface
 */
export interface ExportFile {
  filename: string;
  format: string;
  size: number;
  lastModified: string;
  downloadUrl: string;
}

/**
 * Get exports response interface
 */
export interface GetExportsResponse {
  success: boolean;
  exports: ExportFile[];
  message: string;
}

/**
 * GET authentication headers with JWT token
 * - Returns headers object for axios requests
 *
 * @returns Authorization headers
 */
function getAuthHeaders() {
  const token = getToken();
  return {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
}

/**
 * EXPORT workspace in specified format
 * - Requires authentication and workspace ownership
 * - Generates export file and uploads to MinIO
 * - Returns presigned download URL (24-hour expiry)
 *
 * @param workspaceId Workspace UUID
 * @param format Export format (pdf, png, csv, json)
 * @returns Download URL and filename
 * @throws Error if request fails or user not authorized
 */
export async function exportWorkspace(
  workspaceId: string,
  format: 'pdf' | 'png' | 'csv' | 'json'
): Promise<ExportWorkspaceResponse> {
  try {
    const response = await apiClient.post<ExportWorkspaceResponse>(
      `/api/workspaces/${workspaceId}/export`,
      { format },
      getAuthHeaders()
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.error || 'Failed to export workspace');
    }
    throw new Error('An unexpected error occurred');
  }
}

/**
 * GET all exports for a workspace
 * - Retrieves list of all export files with metadata and download URLs
 * - Used for download management page (US-6.3)
 *
 * @param workspaceId Workspace UUID
 * @returns List of export files with download URLs
 * @throws Error if request fails or user not authorized
 */
export async function getWorkspaceExports(workspaceId: string): Promise<GetExportsResponse> {
  try {
    const response = await apiClient.get<GetExportsResponse>(
      `/api/workspaces/${workspaceId}/exports`,
      getAuthHeaders()
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.error || 'Failed to retrieve exports');
    }
    throw new Error('An unexpected error occurred');
  }
}
